// Minimal standalone player used only by the Song Library marker editor, so
// a drummer can play/scrub a song by ear while dropping structure markers
// (Verse 1, Chorus 1, ...). Deliberately separate from PlaybackController:
// this has no setlist/metronome coordination to do, just play/pause/seek on
// one file at a time. Still routes through the same explicit stereo
// splitter/merger passthrough as everywhere else in the app, so what you hear
// while marking up a song (including the click channel) matches how it will
// actually sound live — this is not a "just for editing, routing doesn't
// matter" shortcut.
//
// Also hosts the synthetic click track tuning tool: for a song whose own
// audio has no click baked in, a generated click can be layered on top via
// SyntheticClickLayer (routed to whichever side the song's own
// syntheticClickSide setting picks) so the drummer can dial in a BPM +
// offset until the click's beat 1 lines up with the song's real downbeat.

import { createExplicitStereoPassthrough, createLeftOnlyRoute, createRightOnlyRoute } from './stereoRouting';
import { SyntheticClickLayer } from './SyntheticClickLayer';
import type { MetronomeSettings } from '../core/types';
import { resumeAudioContextOnVisible } from '../core/audioContextRecovery';

export interface MarkerPlayerEvent {
  type: 'timeupdate' | 'playing' | 'pause' | 'ended' | 'error';
  currentTime: number;
  duration: number;
}

type Listener = (event: MarkerPlayerEvent) => void;

class MarkerEditorPlayer {
  private audioContext: AudioContext | null = null;
  private mediaElement: HTMLAudioElement | null = null;
  private sourceNode: MediaElementAudioSourceNode | null = null;
  private objectUrl: string | null = null;
  private listeners = new Set<Listener>();
  private loadedSongId: string | null = null;

  private clickLayer: SyntheticClickLayer | null = null;
  private clickEnabled = false;
  private clickOffsetSeconds = 0;
  private clickSide: 'left' | 'right' = 'right';

  private getAudioContext(): AudioContext {
    if (!this.audioContext) this.audioContext = new AudioContext();
    return this.audioContext;
  }

  /** Exposes the (possibly not-yet-created) AudioContext for
   * resumeAudioContextOnVisible, below — deliberately read-only and named
   * distinctly from the private getAudioContext() above, which creates one
   * on demand; this must NOT create a context just because the tab changed
   * visibility with no song ever loaded. */
  peekAudioContext(): AudioContext | null {
    return this.audioContext;
  }

  get currentSongId(): string | null {
    return this.loadedSongId;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(event: MarkerPlayerEvent): void {
    for (const listener of this.listeners) listener(event);
  }

  /** Loads a song's audio if not already loaded, paused at time 0. Loading a
   * different song than whatever is currently loaded stops and replaces it —
   * only one song's audio plays at a time across all expanded marker editors.
   * `outputSide` mirrors whatever routing PlaybackController would apply
   * during real performance (see its own loadSong doc comment) so tuning by
   * ear in this editor matches what will actually be heard live. */
  async loadIfNeeded(songId: string, blob: Blob, outputSide: 'stereo' | 'left' | 'right' = 'stereo'): Promise<void> {
    if (this.loadedSongId === songId && this.mediaElement) return;
    this.teardown();

    const context = this.getAudioContext();
    const url = URL.createObjectURL(blob);
    this.objectUrl = url;

    const element = new Audio();
    element.preload = 'auto';
    element.src = url;
    element.loop = false;
    element.autoplay = false;
    this.mediaElement = element;
    this.loadedSongId = songId;

    element.addEventListener('timeupdate', () => {
      this.emit({ type: 'timeupdate', currentTime: element.currentTime, duration: element.duration || 0 });
    });
    element.addEventListener('playing', () => {
      this.emit({ type: 'playing', currentTime: element.currentTime, duration: element.duration || 0 });
    });
    element.addEventListener('pause', () => {
      this.clickLayer?.stop();
      this.emit({ type: 'pause', currentTime: element.currentTime, duration: element.duration || 0 });
    });
    element.addEventListener('ended', () => {
      this.clickLayer?.stop();
      this.emit({ type: 'ended', currentTime: element.currentTime, duration: element.duration || 0 });
    });
    element.addEventListener('error', () => {
      this.clickLayer?.stop();
      this.emit({ type: 'error', currentTime: 0, duration: 0 });
    });

    await new Promise<void>((resolve, reject) => {
      element.addEventListener('loadedmetadata', () => resolve(), { once: true });
      element.addEventListener('error', () => reject(new Error('Failed to load audio for marker editing.')), { once: true });
    });

    const sourceNode = context.createMediaElementSource(element);
    this.sourceNode = sourceNode;
    const route =
      outputSide === 'left'
        ? createLeftOnlyRoute(context)
        : outputSide === 'right'
          ? createRightOnlyRoute(context)
          : createExplicitStereoPassthrough(context);
    sourceNode.connect(route.input);
    route.output.connect(context.destination);
  }

  /** Configures the synthetic click tuning tool. Safe to call at any time,
   * including mid-playback: if the click is currently running and either
   * `enabled` or the offset actually changed, it re-anchors from the current
   * playback position on the new grid (a brief audible re-sync click, not a
   * problem for a tuning tool where the whole point is iterating live). */
  setSyntheticClick(config: { enabled: boolean; settings: MetronomeSettings; offsetSeconds: number; side: 'left' | 'right' }): void {
    const context = this.getAudioContext();
    if (!this.clickLayer) this.clickLayer = new SyntheticClickLayer(context, config.settings, config.side);
    else this.clickLayer.updateSettings(config.settings);

    const sideChanged = config.side !== this.clickSide;
    if (sideChanged) this.clickLayer.updateSide(config.side);
    this.clickSide = config.side;

    const offsetChanged = config.offsetSeconds !== this.clickOffsetSeconds;
    const enabledChanged = config.enabled !== this.clickEnabled;
    this.clickEnabled = config.enabled;
    this.clickOffsetSeconds = config.offsetSeconds;

    if (!this.clickLayer.isRunning) return;
    if (offsetChanged || sideChanged || (enabledChanged && !config.enabled)) {
      this.clickLayer.stop();
      if (config.enabled && this.mediaElement && !this.mediaElement.paused) {
        this.clickLayer.start(this.mediaElement.currentTime, this.clickOffsetSeconds);
      }
    } else if (enabledChanged && config.enabled && this.mediaElement) {
      this.clickLayer.start(this.mediaElement.currentTime, this.clickOffsetSeconds);
    }
  }

  async play(): Promise<void> {
    if (!this.mediaElement) return;
    if (this.audioContext?.state === 'suspended') await this.audioContext.resume();
    await this.mediaElement.play();
    if (this.clickEnabled) this.clickLayer?.start(this.mediaElement.currentTime, this.clickOffsetSeconds);
  }

  pause(): void {
    this.mediaElement?.pause();
    this.clickLayer?.stop();
  }

  seek(timeSeconds: number): void {
    if (!this.mediaElement) return;
    const duration = this.mediaElement.duration || 0;
    this.mediaElement.currentTime = Math.max(0, Math.min(duration, timeSeconds));
    // Re-anchor the click grid to the new position rather than letting it
    // keep counting from the old one — otherwise a seek would leave the
    // click playing against a position the file is no longer at.
    if (this.clickEnabled && !this.mediaElement.paused) this.clickLayer?.reanchor(this.mediaElement.currentTime);
  }

  get currentTime(): number {
    return this.mediaElement?.currentTime ?? 0;
  }

  get duration(): number {
    return this.mediaElement?.duration ?? 0;
  }

  get isPaused(): boolean {
    return this.mediaElement?.paused ?? true;
  }

  /** Stops and releases the currently loaded song, if any. Called when a
   * different song's marker editor is opened/played, or the section unmounts. */
  teardown(): void {
    this.clickLayer?.teardown();
    this.clickLayer = null;
    this.clickEnabled = false;
    if (this.mediaElement) {
      this.mediaElement.pause();
      this.mediaElement.removeAttribute('src');
      this.mediaElement.load();
    }
    this.sourceNode?.disconnect();
    this.sourceNode = null;
    this.mediaElement = null;
    this.loadedSongId = null;
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }
}

// Module-level singleton: only one song's audio plays at a time across every
// expanded marker editor card in Song Library, matching the app's existing
// "only one thing makes sound" invariant used elsewhere (PlaybackController).
export const markerEditorPlayer = new MarkerEditorPlayer();

// See resumeAudioContextOnVisible's own doc comment for why this exists at
// all — this player's AudioContext is lazily created (getAudioContext,
// above), hence peekAudioContext() rather than a direct reference.
resumeAudioContextOnVisible(() => markerEditorPlayer.peekAudioContext());
